import { useState, useRef } from "react";
import { useFormik } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faUserTie, faSave, faArrowLeft, faMagicWandSparkles, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import CustomModal from "../../../../layout/CustomModal";
import CompanyBasicsForm from "../Companies/CompanyBasicsForm";
import PersonBasicsForm from "../People/PersonBasicsForm";
import CompanyForm from "@serie3/freelancesplaceapi/Form/Crm/CompanyForm";
import PersonForm from "@serie3/freelancesplaceapi/Form/Crm/PersonForm";
import crudApi from "@serie3/common/api/crudApi";
import CrmConfig from "@serie3/freelancesplaceapi/Config/Crm/CrmConfig";

const btnBase = {
  display: "inline-flex", alignItems: "center", gap: "8px",
  border: "none", borderRadius: "10px", fontWeight: 600, cursor: "pointer",
};

const inputStyle = {
  width: "100%", padding: "6px 10px", border: "1px solid #e0e0e0",
  borderRadius: "6px", fontSize: "0.82rem", background: "#fff",
  outline: "none", boxSizing: "border-box",
};

const labelStyle = {
  fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.07em", color: "#9e9e9e", marginBottom: "4px", display: "block",
};

const SectionTitle = ({ children }) => (
  <div style={{
    fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.08em", color: "#9e9e9e", marginBottom: "10px",
  }}>
    {children}
  </div>
);

const ItemList = ({ items, setItems, fields, addEmpty }) => {
  const update = (idx, key, val) =>
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [key]: val } : item));
  const remove = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const add = () => setItems(prev => [...prev, addEmpty()]);

  return (
    <div>
      {items.map((item, idx) => (
        <div key={idx} style={{ display: "flex", gap: "6px", marginBottom: "8px", alignItems: "flex-end" }}>
          {fields.map(f => (
            <div key={f.key} style={{ flex: f.flex || 1 }}>
              {idx === 0 && <label style={labelStyle}>{f.label}</label>}
              <input
                style={inputStyle}
                value={item[f.key] || ""}
                onChange={e => update(idx, f.key, e.target.value)}
                placeholder={f.label}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => remove(idx)}
            style={{ ...btnBase, padding: "5px 9px", background: "#ffebee", color: "#c62828", fontSize: "0.78rem", flexShrink: 0, marginBottom: idx === 0 ? "0px" : undefined }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        style={{ ...btnBase, padding: "5px 12px", background: "#e8f0fe", color: "#1a73e8", fontSize: "0.78rem" }}
      >
        <FontAwesomeIcon icon={faPlus} /> Add
      </button>
    </div>
  );
};

const CreateCustomerModal = ({ show, setShow, updateContact, billId }) => {
  const [step, setStep] = useState("choose");
  const [type, setType] = useState(null);
  const createdId = useRef(null);
  const [extracting, setExtracting] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [socialMedia, setSocialMedia] = useState([]);
  const [dupCompany, setDupCompany] = useState(null);

  const hasExtras = addresses.length > 0 || contacts.length > 0 || socialMedia.length > 0;

  const saveExtras = async (entityId, entityType) => {
    const isCompany = entityType === "company";
    const link = isCompany
      ? { kunde: { id: entityId } }
      : { person: { id: entityId } };
    const today = new Date().toISOString().split("T")[0];

    await Promise.all([
      ...addresses
        .filter(a => a.strasse || a.ort)
        .map(({ id: _id, ...a }) =>
          crudApi.createNew(CrmConfig.address.endpoints.new, JSON.stringify({ ...a, ...link }, null, 2))
        ),
      ...contacts
        .filter(c => c.key_name || c.value)
        .map(({ id: _id, ...c }) =>
          crudApi.createNew(CrmConfig.contactData.endpoints.new, JSON.stringify({ ...c, ...link, date: today }, null, 2))
        ),
      ...socialMedia
        .filter(s => s.key_name || s.value)
        .map(({ id: _id, ...s }) =>
          crudApi.createNew(CrmConfig.socialMedia.endpoints.new, JSON.stringify({ ...s, ...link, date: today }, null, 2))
        ),
    ]);
  };

  const companyFormik = useFormik({
    ...CompanyForm.createCompanyForm(),
    validate: (values) => {
      const errors = {};
      if (!values.firmenname?.trim()) errors.firmenname = "Company name is required";
      return errors;
    },
    onSubmit: async (values) => {
      const response = await crudApi.edit("/crm/company/edit", JSON.stringify(values, null, 2));
      if (response.status === 409) {
        setDupCompany(response.data?.existing ?? response.data);
        return;
      }
      if (response.status === 200) {
        await saveExtras(createdId.current, "company");
        await updateContact({ ...values, id: createdId.current });
        handleClose();
      }
    },
  });

  const forceCompanySubmit = async () => {
    const values = { ...companyFormik.values, forced: true };
    const response = await crudApi.edit("/crm/company/edit", JSON.stringify(values, null, 2));
    if (response.status === 200) {
      setDupCompany(null);
      await saveExtras(createdId.current, "company");
      await updateContact({ ...values, id: createdId.current });
      handleClose();
    }
  };

  const personFormik = useFormik({
    ...PersonForm.createPersonForm(),
    onSubmit: async (values) => {
      const response = await crudApi.edit("/crm/person/edit", JSON.stringify(values, null, 2));
      if (response.status === 200) {
        await saveExtras(createdId.current, "person");
        await updateContact({ ...values, id: createdId.current });
        handleClose();
      }
    },
  });

  const handleClose = () => {
    setStep("choose");
    setType(null);
    createdId.current = null;
    setExtracting(false);
    setAddresses([]);
    setContacts([]);
    setSocialMedia([]);
    setDupCompany(null);
    companyFormik.resetForm();
    personFormik.resetForm();
    setShow(false);
  };

  const handleCreate = async (entityType) => {
    const endpoint = entityType === "company"
      ? CrmConfig.company.index.endpoint
      : CrmConfig.people.index.endpoint;
    const payload = entityType === "person" ? { vorname: " ", name: " " } : {};

    const response = await crudApi.createNew(endpoint, payload);
    if (response.status === 200) {
      const data = response.data;
      createdId.current = data.id;
      setType(entityType);
      if (entityType === "company") {
        companyFormik.setValues(CompanyForm.setEditData(data));
      } else {
        personFormik.setValues(PersonForm.setEditData(data));
      }
      setStep("editing");
    }
  };

  const handleSave = () => {
    if (type === "company") companyFormik.handleSubmit();
    else personFormik.handleSubmit();
  };

  const extractData = async () => {
    setExtracting(true);
    const res = await crudApi.justGet("/crm/ai/extract-from-bill/" + billId);
    if (res?.status === 200 && res.data) {
      const data = res.data;
      const entityType = data.type === "company" ? "company" : "person";
      await handleCreate(entityType);
      if (entityType === "company") {
        const company = data.company || {};
        companyFormik.setValues({
          ...CompanyForm.setEditData({ ...company, tax_id: company.taxId }),
          id: createdId.current,
        });
      } else {
        const person = data.person || {};
        personFormik.setValues({ ...PersonForm.setEditData(person), id: createdId.current });
      }
      if (Array.isArray(data.adressen) && data.adressen.length > 0) setAddresses(data.adressen);
      if (Array.isArray(data.kontaktdaten) && data.kontaktdaten.length > 0) setContacts(data.kontaktdaten);
      if (Array.isArray(data.sozialeMedien) && data.sozialeMedien.length > 0) setSocialMedia(data.sozialeMedien);
    }
    setExtracting(false);
  };

  const modalSize = step === "editing" && hasExtras ? "xl" : "lg";

  return (
    <CustomModal show={show} setShow={handleClose} title="New Contact" size={modalSize}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ padding: "24px" }}>

        {step === "choose" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <p style={{ margin: "0 0 8px", fontSize: "0.88rem", color: "#616161" }}>
              What type of customer do you want to create?
            </p>
            <button
              type="button"
              style={{ ...btnBase, padding: "16px 20px", background: "#e8f0fe", color: "#1a73e8", justifyContent: "flex-start", fontSize: "0.95rem" }}
              onClick={() => handleCreate("company")}
            >
              <FontAwesomeIcon icon={faBuilding} style={{ fontSize: "1.1rem" }} /> Company
            </button>
            <button
              type="button"
              style={{ ...btnBase, padding: "16px 20px", background: "#f3e8fd", color: "#7b1fa2", justifyContent: "flex-start", fontSize: "0.95rem" }}
              onClick={() => handleCreate("person")}
            >
              <FontAwesomeIcon icon={faUserTie} style={{ fontSize: "1.1rem" }} /> Person
            </button>

            {billId && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ flex: 1, height: "1px", background: "#e0e0e0" }} />
                  <span style={{ fontSize: "0.75rem", color: "#9e9e9e", fontWeight: 600 }}>or</span>
                  <div style={{ flex: 1, height: "1px", background: "#e0e0e0" }} />
                </div>
                <button
                  type="button"
                  disabled={extracting}
                  onClick={extractData}
                  style={{ ...btnBase, padding: "16px 20px", background: "#fff8e1", color: "#f57c00", justifyContent: "flex-start", fontSize: "0.95rem", opacity: extracting ? 0.7 : 1 }}
                >
                  {extracting ? (
                    <><span style={{ width: "16px", height: "16px", border: "2px solid #ffe0b2", borderTopColor: "#f57c00", borderRadius: "50%", animation: "spin 0.75s linear infinite", display: "inline-block" }} /> Extracting…</>
                  ) : (
                    <><FontAwesomeIcon icon={faMagicWandSparkles} style={{ fontSize: "1.1rem" }} /> Extract data</>
                  )}
                </button>
              </>
            )}
          </div>
        )}

        {step === "editing" && (
          <div style={{ display: "flex", gap: "28px" }}>

            {/* Left: basic entity form */}
            <div style={{ flex: "0 0 320px" }}>
              {type === "company"
                ? <CompanyBasicsForm formik={companyFormik} />
                : <PersonBasicsForm formik={personFormik} />
              }
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
                <button
                  type="button"
                  onClick={() => { setStep("choose"); setType(null); createdId.current = null; }}
                  style={{ ...btnBase, padding: "9px 16px", background: "#f5f5f5", color: "#616161", fontSize: "0.85rem" }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Back
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  style={{ ...btnBase, padding: "9px 18px", background: "#1a73e8", color: "#fff", fontSize: "0.88rem" }}
                >
                  <FontAwesomeIcon icon={faSave} /> Save & Select
                </button>
              </div>
            </div>

            {/* Right: extracted extras */}
            {hasExtras && (
              <div style={{ flex: 1, borderLeft: "1px solid #ececec", paddingLeft: "28px" }}>
                {addresses.length > 0 && (
                  <div style={{ marginBottom: "24px" }}>
                    <SectionTitle>Address</SectionTitle>
                    <ItemList
                      items={addresses}
                      setItems={setAddresses}
                      addEmpty={() => ({ strasse: "", hausnummer: "", plz: "", ort: "" })}
                      fields={[
                        { key: "strasse", label: "Street", flex: 2 },
                        { key: "hausnummer", label: "No.", flex: 0.6 },
                        { key: "plz", label: "ZIP", flex: 0.8 },
                        { key: "ort", label: "City", flex: 1.4 },
                      ]}
                    />
                  </div>
                )}
                {contacts.length > 0 && (
                  <div style={{ marginBottom: "24px" }}>
                    <SectionTitle>Contact Data</SectionTitle>
                    <ItemList
                      items={contacts}
                      setItems={setContacts}
                      addEmpty={() => ({ key_name: "", value: "" })}
                      fields={[
                        { key: "key_name", label: "Type" },
                        { key: "value", label: "Value", flex: 2 },
                      ]}
                    />
                  </div>
                )}
                {socialMedia.length > 0 && (
                  <div>
                    <SectionTitle>Social Media</SectionTitle>
                    <ItemList
                      items={socialMedia}
                      setItems={setSocialMedia}
                      addEmpty={() => ({ key_name: "", value: "" })}
                      fields={[
                        { key: "key_name", label: "Platform" },
                        { key: "value", label: "URL", flex: 2 },
                      ]}
                    />
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        {dupCompany && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 20,
            background: "rgba(255,255,255,0.97)", borderRadius: "0 0 14px 14px",
            overflowY: "auto", padding: "24px",
          }}>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#c62828", marginBottom: "4px" }}>
                Company already exists
              </div>
              <div style={{ fontSize: "0.82rem", color: "#616161" }}>
                The following company was found. Create a duplicate anyway?
              </div>
            </div>

            {/* Company card */}
            <div style={{ background: "#f8f9fb", borderRadius: "10px", padding: "14px 16px", marginBottom: "14px", border: "1px solid #e0e0e0" }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#212121", marginBottom: "2px" }}>{dupCompany.firmenname}</div>
              {dupCompany.rechtsform && <div style={{ fontSize: "0.78rem", color: "#1a73e8", marginBottom: "6px" }}>{dupCompany.rechtsform}</div>}
              {dupCompany.kundenklasse && <div style={{ fontSize: "0.78rem", color: "#616161" }}>Class: {dupCompany.kundenklasse}</div>}
            </div>

            {/* Addresses */}
            {dupCompany.adresse?.length > 0 && (
              <div style={{ marginBottom: "12px" }}>
                <div style={labelStyle}>Addresses</div>
                {dupCompany.adresse.map((a, i) => (
                  <div key={i} style={{ fontSize: "0.82rem", color: "#424242", padding: "4px 0", borderBottom: "1px solid #f0f0f0" }}>
                    {[a.strasse, a.hausnummer, a.plz, a.ort].filter(Boolean).join(" ")}
                    {a.title && <span style={{ color: "#9e9e9e", marginLeft: "6px" }}>· {a.title}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Contact data */}
            {dupCompany.kontaktdaten?.length > 0 && (
              <div style={{ marginBottom: "12px" }}>
                <div style={labelStyle}>Contact</div>
                {dupCompany.kontaktdaten.map((c, i) => (
                  <div key={i} style={{ fontSize: "0.82rem", color: "#424242", padding: "4px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <span style={{ color: "#9e9e9e", marginRight: "6px" }}>{c.key_name}:</span>{c.value}
                  </div>
                ))}
              </div>
            )}

            {/* Social media */}
            {dupCompany.soziale_medien?.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div style={labelStyle}>Social Media</div>
                {dupCompany.soziale_medien.map((s, i) => (
                  <div key={i} style={{ fontSize: "0.82rem", color: "#424242", padding: "4px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <span style={{ color: "#9e9e9e", marginRight: "6px" }}>{s.key_name}:</span>{s.value}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                onClick={() => setDupCompany(null)}
                style={{ ...btnBase, padding: "9px 20px", background: "#f5f5f5", color: "#616161", fontSize: "0.88rem" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={forceCompanySubmit}
                style={{ ...btnBase, padding: "9px 20px", background: "#e53935", color: "#fff", fontSize: "0.88rem" }}
              >
                Create anyway
              </button>
            </div>
          </div>
        )}

      </div>
    </CustomModal>
  );
};

export default CreateCustomerModal;
